/**
 * The wire contract for camera-decoded ID check-in: the path, the payload, the response, the
 * envelope it arrives in, and the copy both surfaces show when a decode does not produce one.
 *
 * This stays the single import site for both surfaces. Response routing lives in
 * `scan-entry-result.ts`, the optional front-photo concern in `camera-id-photo.ts`, whether a
 * profile already has a front-of-ID photo in `id-photo-presence.ts`, and the typed fields staff
 * enter when a barcode will not decode in `typed-id-entry.ts`; all four are re-exported below.
 *
 * Typed entry has no endpoint of its own and none is declared here. The route the old manual form
 * posted to (`checkin/ocr-scan-entry`) and its DTO, verifier and manual-only `GuestEntryService`
 * behaviour were removed in leaflogix #22416, so a typed fallback reaches the same create paths
 * each surface already has rather than a second compliance pipeline.
 */
import type { IdFieldName } from './typed-id-entry';

/**
 * The decoded PDF417 payload, on Register.
 *
 * Register-only server-side (leaflogix #22416). Authorization is the route, not the body: the
 * `Surface` field below no longer selects which location feature is checked, so a mobile-checkout
 * client posting here would be refused as Register regardless of what it stamped on the payload.
 */
export const CAMERA_SCAN_ENTRY_PATH = 'checkin/camera-scan-entry';

/**
 * The same decoded payload, on mobile checkout — a separate route the server authorizes as
 * Mobile-only, not a query-string or body variant of the Register one.
 *
 * A literal of its own rather than `checkin/mobile-checkout/${...}` off the constant above, for the
 * reason `camera-id-photo.ts` gives: the two are independent server routes that happen to read
 * alike, and deriving one from the other would make a Register route change move Mobile's with it.
 */
export const MOBILE_CHECKOUT_CAMERA_SCAN_ENTRY_PATH = 'checkin/mobile-checkout/camera-scan-entry';

export { decodeScanEntry, enteredCustomerId } from './scan-entry-result';
export type { EnteredEntry, ScanEntryOutcome } from './scan-entry-result';

export {
  FRONT_PHOTO_CAMERA_ACTION,
  FRONT_PHOTO_HEADING,
  FRONT_PHOTO_HINT,
  FRONT_PHOTO_SHUTTER_ACTION,
  FRONT_PHOTO_SKIP_ACTION,
  FRONT_PHOTO_UPLOAD_ACTION,
  ID_PHOTO_COMPRESSION_OPTIONS,
  ID_PHOTO_PATH,
  ID_PHOTO_SAVE_FAILURE_MESSAGE,
  ID_PHOTO_WRONG_SIDE_MESSAGE,
  MOBILE_CHECKOUT_ID_PHOTO_PATH,
  frontPhotoCameraErrorMessage,
  photoSaveFailureMessage,
  toIdImage,
  toIdPhotoPayload,
  unwrapIdPhotoResult,
} from './camera-id-photo';
export type { IdImage, IdPhotoEnvelope, IdPhotoPayload, IdPhotoResult } from './camera-id-photo';

export {
  ID_DATE_FIELDS,
  ID_EDITABLE_FIELDS,
  ID_FIELD_LABELS,
  ID_FIELD_NAMES,
  ID_REQUIRED_FIELDS,
  MEDICAL_CUSTOMER_TYPE_ID,
  RECREATIONAL_CUSTOMER_TYPE_ID,
  missingRequiredFields,
  selectCustomerTypeId,
  toDateString,
  toLocalDate,
  toTypedIdCreateFields,
  toTypedIdDraftOverrides,
} from './typed-id-entry';
export type {
  ConfiguredCustomerType,
  IdDocumentType,
  IdFieldName,
  IdFieldValues,
  SelectCustomerTypeIdArgs,
  TypedIdCreateFields,
  TypedIdCreateInput,
  TypedIdDraftOverrides,
} from './typed-id-entry';

export {
  frontPhotoTarget,
  needsFrontIdPhoto,
  needsFrontIdPhotoForCandidate,
  newProfileNeedsFrontIdPhoto,
} from './id-photo-presence';
export type { IdPhotoPresence, PresenceCandidate } from './id-photo-presence';

/**
 * What the scan read, as the server parsed it out of the AAMVA payload.
 *
 * Named against `IdFieldName` — the one list of fields a card can yield, which
 * `typed-id-entry.ts` owns because a typed entry and a decode have to agree on them. Still open
 * (`Record<string, unknown>`) rather than closed: the server projects fields this client has no
 * name for, and refusing to carry them would lose data the discrepancy layer may want. Nothing in
 * *this* layer reads an individual field.
 */
export type ScanIdentification = Partial<Record<IdFieldName, string | null>> &
  Record<string, unknown> & { IsMedicalId?: boolean };

export type ScanProfileDiscrepancy = {
  Field: string;
  CurrentValue: string | null;
  ScannedValue: string | null;
};

export type ScanValidatedCustomer = {
  Id: number;
  Status: string | null;
  MJStateIdNumber: string | null;
  MJStateIdStartDate: string | null;
  MJStateIdExpirationDate: string | null;
  DriversLicenseNumber: string | null;
  DriversLicenseExpirationDate: string | null;
  FullName: string | null;
  NamePrefix: string | null;
  FirstName: string | null;
  MiddleName: string | null;
  LastName: string | null;
  NameSuffix: string | null;
  DateOfBirth: string | null;
  Address1: string | null;
  Address2: string | null;
  City: string | null;
  State: string | null;
  PostalCode: string | null;
  CustomerType: string | null;
  IsMedical: boolean;
  Nickname: string | null;
  IsLoyaltyMember: boolean;
  LoyaltyPoints: number | null;
  LastTransaction: string | null;
  PhoneNumber: string | null;
  Email: string | null;
  ProfileCompleteness: number | null;
  Accounts: string[] | null;
  Discrepancies: ScanProfileDiscrepancy[] | null;
  IsBanned: boolean;
  IsUnderMedicalAge: boolean;
  IsUnderRecreationalAge: boolean;
  IsMJStateIdExpired: boolean;
  IsDriversLicenseExpired: boolean;
  /**
   * Whether this profile already has a front-of-ID photo. Optional and nullable, unlike the fields
   * above: it is new on the response, so an older backend and a candidate the server did not
   * project it for both arrive without it, and neither means the profile has no photo.
   *
   * `id-photo-presence.ts` is the only place that decides what each of the three answers means.
   * Nothing should read this field directly.
   */
  HasIdPhoto?: boolean | null;
};

/** The check-in pipeline's answer. `scan-entry-result.ts` documents how it routes. */
export type ScanGuestEntryResponse = {
  PosRegisterId: number | null;
  /** The location has auto-check-in off: staff pick a match or create a profile. */
  RequiresUserDecision?: boolean;
  CreatedCustomerId?: number | null;
  CheckedInShipmentId?: number | null;
  // Informational registry warning that never changes the check-in outcome.
  RegistryCheckMessage?: string | null;
  /**
   * Who the pipeline actually resolved this scan to, said by the server rather than inferred here:
   * the profile it created, or the ranked match it actually checked in (leaflogix #22416, camera
   * source only).
   *
   * Optional because it is serialised null-omitted, so an explicit decision — where the server
   * deliberately resolves nobody, because staff have not chosen yet — and a deployment that predates
   * the field are the same absent key on the wire. Both mean "no authoritative identity", which is
   * why `frontPhotoTarget` treats an absent anchor as a refusal for an existing customer rather than
   * falling back to picking a candidate.
   */
  ResolvedCustomerId?: number | null;
  IdentificationData: ScanIdentification | null;
  ValidatedIdentificationCustomer: ScanValidatedCustomer | null;
  ValidatedMatchedCustomers: ScanValidatedCustomer[] | null;
  ValidatedMatchedCaregivers: ScanValidatedCustomer[] | null;
};

/**
 * Which surface a request came from, as the server records it.
 *
 * No longer what authorization turns on: leaflogix #22416 moved that onto the route, so the two
 * pairs of paths above are what decide whether a caller is let in. Still stamped, and still
 * required by the type, for wire compatibility with a server that has not dropped the field from
 * its contract.
 */
export type ScanSurface = 'Register' | 'MobileCheckout';

/** Each surface's query module is the one place its own `Surface` is set. */
export type Surfaced<TPayload> = TPayload & { Surface: ScanSurface };

/** `ID` is the decoded AAMVA payload, byte for byte, parsed server-side as a hardware scan is. */
export type CameraScanEntryPayload = {
  ID: string;
  RegisterId: number;
};

/**
 * What staff are told when a read does not produce a payload. Every one of these ends in an action
 * that still works — retry, or upload a photo of the barcode — because a dead end at the counter is
 * worse than a slower path. The wording names only the paths that exist on the surfaces today;
 * whichever layer actually puts a typed form in front of staff is the one that should offer it here.
 */

/** The upload decoded nothing. One still gets one attempt, so this is common rather than exceptional. */
export const UPLOAD_NO_BARCODE_MESSAGE = 'No barcode was found in that image. Try again with a clearer photo.';

/** The browser could not read the file as an image — HEIC off an iPhone, or over the size ceiling. */
export const UPLOAD_UNSUPPORTED_MESSAGE = 'That image could not be read. Try a JPEG or PNG.';

export const ID_SCAN_UNKNOWN_MESSAGE = 'The ID could not be read. Try again, or upload a photo of the barcode.';

export const ID_SCAN_NO_DECODER_MESSAGE = 'This device cannot scan ID barcodes. Upload a photo of the barcode instead.';

/** No idempotency key: a failure may have checked someone in, so a retry can duplicate them. */
export const ID_SCAN_ENTRY_FAILURE_MESSAGE =
  'We could not confirm the check-in. Check the guest list before trying again — the customer may already be checked in.';

/** The `ApiResult` envelope every one of these endpoints answers in. */
export type ScanEnvelope<TData> = {
  Result: boolean;
  Message?: string | null;
  Data?: TData | null;
};

/** Fail-closed: a refusal may still carry a payload, so reading `Data` alone would read it as a success. */
export const unwrapScanEntry = <TData>(envelope: ScanEnvelope<TData> | null | undefined): TData => {
  if (envelope?.Result && envelope.Data) {
    return envelope.Data;
  }

  throw new Error(envelope?.Message?.trim() || ID_SCAN_UNKNOWN_MESSAGE);
};
